import { useState } from "react";

import { useAtom } from "jotai";
import {
  ChevronsUpDown,
  Eye,
  EyeOff,
  LogOut,
  MoonStar,
  Settings as SettingsIcon,
  Shield,
} from "lucide-react";

import { navigate, routes } from "@redwoodjs/router";

import { settingsDialogAtom } from "src/components/SettingsDialog/SettingsDialog";
import { Avatar, AvatarFallback, AvatarImage } from "src/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "src/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "src/components/ui/sidebar";
import { logOutLocally, useAuth } from "src/lib/auth";
import { useAdminPrivacyMode } from "src/lib/hooks/useAdminPrivacyMode";
import { useTheme } from "src/lib/theme/ThemeProvider";

export function UserNav() {
  const { isMobile } = useSidebar();
  const { currentUser = {} } = useAuth();
  const { theme, setTheme } = useTheme();
  const { isAdmin, privacyMode, setPrivacyMode } = useAdminPrivacyMode();

  const [menuOpen, setMenuOpen] = useState(false);
  const [, setSettingsDialogState] = useAtom(settingsDialogAtom);

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu
            open={menuOpen}
            onOpenChange={setMenuOpen}
            modal={isMobile}
          >
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                onClick={(e) => e.stopPropagation()}
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground cursor-pointer data-[sidebar-open=false]:rounded-full"
              >
                <Avatar className="h-8 w-8 rounded-full">
                  <AvatarImage
                    src={currentUser.picture}
                    alt={currentUser.name}
                  />
                  <AvatarFallback>
                    {currentUser.name
                      ?.split(" ")
                      .map((n) => n[0].toUpperCase())
                      .join("")}
                  </AvatarFallback>
                </Avatar>

                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">
                    {currentUser.name}
                  </span>
                  <span className="truncate text-xs">{currentUser.email}</span>
                </div>

                <ChevronsUpDown className="ml-auto size-4" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>

            <DropdownMenuContent
              className="min-w-56 rounded-lg"
              side={isMobile ? "bottom" : "right"}
              align="end"
              sideOffset={4}
              onCloseAutoFocus={(e) => e.preventDefault()}
            >
              <DropdownMenuLabel className="p-0 font-normal">
                <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                  <Avatar className="size-8 rounded-full">
                    <AvatarImage
                      src={currentUser.picture}
                      alt={currentUser.name}
                    />
                    <AvatarFallback>
                      {currentUser.name
                        ?.split(" ")
                        .map((n) => n[0].toUpperCase())
                        .join("")}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">
                      {currentUser.name}
                    </span>
                    <span className="truncate text-xs">
                      {currentUser.email}
                    </span>
                  </div>
                </div>
              </DropdownMenuLabel>

              <DropdownMenuSeparator />

              <DropdownMenuGroup>
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    setSettingsDialogState((prev) => ({
                      ...prev,
                      open: true,
                    }));
                  }}
                >
                  Settings
                  <DropdownMenuShortcut>
                    <SettingsIcon className="size-4" />
                  </DropdownMenuShortcut>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    setTheme(theme === "dark" ? "light" : "dark");
                  }}
                >
                  Theme
                  <DropdownMenuShortcut>
                    <MoonStar className="size-4" />
                  </DropdownMenuShortcut>
                </DropdownMenuItem>
              </DropdownMenuGroup>

              {isAdmin && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuGroup>
                    <DropdownMenuItem
                      onSelect={(e) => {
                        e.preventDefault();
                        navigate(routes.admin());
                      }}
                    >
                      Admin
                      <DropdownMenuShortcut>
                        <Shield className="size-4" />
                      </DropdownMenuShortcut>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={(e) => {
                        e.preventDefault();
                        setPrivacyMode(!privacyMode);
                      }}
                    >
                      Privacy mode
                      <DropdownMenuShortcut>
                        {privacyMode ? (
                          <EyeOff className="size-4" />
                        ) : (
                          <Eye className="size-4" />
                        )}
                      </DropdownMenuShortcut>
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                </>
              )}

              <DropdownMenuSeparator />

              <DropdownMenuItem
                onSelect={async (e) => {
                  e.preventDefault();
                  try {
                    await logOutLocally();
                    window.location.assign(routes.login());
                  } catch (error) {
                    console.error("Unable to log out:", error);
                  }
                }}
              >
                Log out
                <DropdownMenuShortcut>
                  <LogOut className="size-4" />
                </DropdownMenuShortcut>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
    </>
  );
}
